import styles from './styles.module.scss';
import { UpgradeActions, UpgradeStatus, UpgradeType } from '../../types/game-types';
import { useGameDispatch } from '../../store/global-context';
import { Tooltip } from 'radix-ui';
import { TreeNodeData } from 'skill-tree/src/models';
import { SmallButton } from '../../gui/components/SmallButton';
import { ComposableIcon } from '@game-libs/exc-libs/src/composable-icons';
import { useItemCost } from '../../store/hooks/useItemCost';
import { useGeneratorContext } from '../../store/generator-context';
import { useUpgradesStore } from '../../store/zustand/useUpgradesStore';

export const UpgradeNode = ({ skill }: { skill: TreeNodeData }) => {
  const dispatch = useGameDispatch();
  const entityType = useGeneratorContext();
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const upgrade = upgrades[entityType][skill.id as UpgradeType];
  const [, , tooExpensive] = useItemCost(upgrade);

  const upgradeStatus = upgrade.status || UpgradeStatus.Hidden;

  const isMaxed = upgrade.purchased >= (upgrade.purchaseLimit || Infinity);
  const isPurchaseable = upgrade.status === UpgradeStatus.Unlocked && !tooExpensive;
  const isLockedOnDemo = upgrade.isLockedOnDemo || false;
  const isLocked = upgradeStatus.valueOf() === UpgradeStatus.Locked;
  const isHidden = upgradeStatus.valueOf() === UpgradeStatus.Hidden;

  if (isHidden) {
    return null;
  }

  const onPurchaseUpgrade = () => {
    if (isMaxed || !isPurchaseable || isLockedOnDemo) {
      return;
    }
    dispatch({ type: UpgradeActions.BUY_UPGRADE, payload: { id: skill.id, entityType } });

    const defaultUnlockAtValue = 3;
    const unlockAmountPurchased = upgrade.purchased + 1 === (skill.subTreeConfig?.unlockAt ?? defaultUnlockAtValue);

    if (upgrade.purchased === 0) {
      dispatch({ type: UpgradeActions.SHOW_UPGRADE, payload: { children: skill.children, entityType } });
    }
    if (unlockAmountPurchased) {
      dispatch({ type: UpgradeActions.UNLOCK_UPGRADE, payload: { children: skill.children, entityType } });
    }
  };

  let buttonType: 'gray' | 'red' | 'green' = 'gray';

  if (tooExpensive || isLockedOnDemo) {
    buttonType = 'red';
  } else if (isMaxed || isLocked || isHidden) {
    buttonType = 'gray';
  } else {
    buttonType = 'green';
  }

  let upgradeIcon: string[] = [];
  if (isLockedOnDemo) {
    upgradeIcon = ['l_s_lockRed'];
  } else if (isLocked) {
    upgradeIcon = ['l_s_lock'];
  } else {
    upgradeIcon = upgrade.icon || [];
  }

  return (
    <div
      className={`game-tooltip hover-target ${styles.upgradeTrigger} ${styles[upgradeStatus]} ${isMaxed && styles['isMaxed']} ${!isPurchaseable && styles['isNotPurchaseable']}`}
      data-testid={`upgrade-node-${skill.id}`}
    >
      <Tooltip.Trigger
        asChild
        onClick={(event) => event.preventDefault()}
        onPointerDown={(event) => event.preventDefault()}
      >
        <SmallButton hoverable={!isMaxed} type={buttonType} onClick={onPurchaseUpgrade}>
          <ComposableIcon iconConfig={upgradeIcon} />
        </SmallButton>
      </Tooltip.Trigger>
    </div>
  );
};
